import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Clock, Check, X, FileText, ShieldCheck } from "lucide-react";

const KIND_LABEL = {
  national_id: "National ID",
  business_registration: "Business registration",
  proof_of_address: "Proof of address",
};

const STATUS_PILL = {
  in_review: { cls: "badge-review", Icon: Clock, key: "kyc_in_review" },
  approved:  { cls: "badge-success", Icon: Check, key: "kyc_approved" },
  rejected:  { cls: "badge-rejected", Icon: X, key: "kyc_rejected" },
  pending:   { cls: "badge-pending", Icon: Clock, key: "kyc_pending" },
};

function StepDot({ tone = "muted" }) {
  const colours = {
    success: "bg-[#114232] ring-[#114232]/20",
    danger: "bg-[#c15337] ring-[#c15337]/20",
    pending: "bg-[#d49a36] ring-[#d49a36]/30",
    muted: "bg-[#d8d8d3] ring-[#d8d8d3]/40",
  };
  return <span className={`w-2.5 h-2.5 rounded-full ring-4 ${colours[tone]}`} />;
}

export default function MerchantDocTimeline() {
  const { t } = useTranslation();
  const [docs, setDocs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/merchant/kyc-documents")
      .then((r) => setDocs(r.data.documents || []))
      .finally(() => setLoaded(true));
  }, []);

  if (loaded && docs.length === 0) return null; // no timeline if nothing submitted

  return (
    <div
      className="bg-white border border-[#114232]/10 rounded-lg p-6"
      data-testid="merchant-doc-status-timeline"
    >
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-4 h-4 text-[#114232]" />
        <div className="font-heading text-base font-semibold text-[#114232]">
          {t("merchant_dashboard.kyc_timeline_title", "KYC document history")}
        </div>
      </div>
      <ul className="space-y-5" data-testid="merchant-doc-timeline-list">
        {docs.map((d) => {
          const meta = STATUS_PILL[d.status] || STATUS_PILL.in_review;
          const { Icon } = meta;
          const submittedTone =
            d.status === "rejected" ? "danger"
              : d.status === "approved" ? "success"
                : "pending";
          const reviewedTone =
            d.status === "approved" ? "success"
              : d.status === "rejected" ? "danger"
                : "muted";
          return (
            <li
              key={d.id}
              className="flex gap-4 items-start"
              data-testid={`merchant-doc-timeline-item-${d.id}`}
            >
              <FileText className="w-4 h-4 text-[#5c6360] mt-1 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm font-semibold text-[#1b1d1c]">
                    {KIND_LABEL[d.kind] || d.kind.replace(/_/g, " ")}
                  </div>
                  <span
                    data-testid={`merchant-doc-timeline-status-${d.id}`}
                    className={`badge-status ${meta.cls} inline-flex items-center gap-1`}
                  >
                    <Icon className="w-3 h-3" /> {t(`merchant_dashboard.${meta.key}`)}
                  </span>
                </div>

                {/* Steps */}
                <ol className="mt-2 space-y-1.5 text-xs">
                  <li className="flex items-center gap-3">
                    <StepDot tone={submittedTone} />
                    <span className="text-[#5c6360]">
                      Submitted ·{" "}
                      <span className="font-mono text-[#1b1d1c]">
                        {new Date(d.submitted_at).toLocaleString()}
                      </span>
                    </span>
                  </li>
                  {(d.reviewed_at || d.status !== "in_review") && (
                    <li className="flex items-center gap-3">
                      <StepDot tone={reviewedTone} />
                      <span className="text-[#5c6360]">
                        {d.status === "approved"
                          ? "Approved"
                          : d.status === "rejected"
                            ? "Rejected"
                            : "Pending review"}
                        {d.reviewed_at && (
                          <>
                            {" · "}
                            <span className="font-mono text-[#1b1d1c]">
                              {new Date(d.reviewed_at).toLocaleString()}
                            </span>
                          </>
                        )}
                        {d.reviewer_email && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-[#5c6360]/80">
                            by {d.reviewer_email}
                          </span>
                        )}
                      </span>
                    </li>
                  )}
                  {d.review_reason && (
                    <li className="ml-5 text-[#8b3a23] bg-[#c15337]/8 border border-[#c15337]/20 rounded-md px-2.5 py-1.5">
                      {d.review_reason}
                    </li>
                  )}
                </ol>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
